import { Component, type OnInit, ViewChild } from '@angular/core'
import { Router } from '@angular/router'
import { NgbAccordion } from '@ng-bootstrap/ng-bootstrap'
import { select, Store } from '@ngrx/store'
import * as lodash from 'lodash'
import {
  ChannelNameTokens,
  ChannelTypes,
  DataCheckerService,
  Destroyable,
  HelpVideoDialogService,
  isSomething,
  selectChannelByName,
  selectCurrentAccount,
  selectSomeOnce,
  untilDestroy,
  updateAccountAttributes,
} from '@tokeet-frontend/tv3-platform'
import { AIResponderSettingsService } from '@tv3/containers/ai-responder/settings/ai-responser-settings.service'
import { ChannelConnectWizardsService } from '@tv3/containers/channels/channel-connect/channel-connect-wizards/index.service'
import { filter, map, switchMap, tap } from 'rxjs/operators'
import { DownloadAppService } from './download-app/download-app.service'
import { SubscribeRategenieService } from './subscribe-rategenie/subscribe-rategenie.service'
import { selectAPIConnectionsByChannelName } from '@tv3/store/connection/connection.selectors'
import {
  AddAutomation,
  AutomationEventTypes,
  AutomationHandler,
  AutomationHelpers,
  AutomationTypes,
  AutomationViewGroup,
  selectAutomationGroupsByEvent,
} from '@tokeet-frontend/automations'
import { selectAISettings } from '@tv3/containers/ai-responder/store/ai-settings.selectors'
import { selectAllVendors, VendorsGuard } from '@tokeet-frontend/vendors'
import { selectAllWebsites } from '@tv3/store/website/website.selectors'
import { selectActiveProductPlan } from '@tv3/store/plan/plan.selectors'
import { isSubscriptionValid, ProductsForPlan } from '@tv3/store/plan/plan.model'
import { combineLatest } from 'rxjs'
import { AISettingsGuard } from '@tv3/containers/ai-responder/store/ai-settings.guard'
import { WebsiteGuard } from '@tv3/guards/website.guard'
import { ConnectionGuard } from '@tv3/guards/connection.guard'
import {
  OpenAddVendorDialog,
  OpenAddWebsiteDialog,
  OpenAutomationWizardOverlay,
} from '@tv3/store/overlay/overlay.actions'
import { WebsiteTypes } from '@tv3/store/website/website.model'
import { GoogleTagManagerService } from '@tv3/services/google-tag-manager.service'
import { AmplitudeService } from '@tv3/services/amplitude.service'
import * as moment from 'moment'
import { Actions, ofType } from '@ngrx/effects'

type OnboardingTask = {
  id: string
  title: string
  description?: string
  ctaIcon?: string
  ctaIconImage?: string
  ctaText: string
  route?: string
  action?: () => void
  completed?: boolean
  pretaskId?: string
  disabledTooltip?: string
  noSkip?: boolean
  completedEvent: string
  skippedEvent?: string
  canceledEvent?: string
  async?: boolean
}

type OnboardingSection = {
  title: string
  icon: string
  videoImage: string
  videoId: string
  tasks: OnboardingTask[]
}

@Component({
  selector: 'app-account-onboarding-card',
  templateUrl: './account-onboarding-card.html',
  styleUrls: ['./account-onboarding-card.scss'],
})
export class AccountOnboardingCard extends Destroyable implements OnInit {
  @ViewChild('taskAccordion') taskAccordion?: NgbAccordion
  private completedTaskIds: string[] = []

  readonly sections: OnboardingSection[] = [
    {
      title: 'Set up your channels',
      icon: 'assets/images/onboarding/step1.svg',
      videoImage: 'assets/images/onboarding/connect-airbnb.png',
      videoId: 'BvXjwBfOvuc',
      tasks: [
        {
          id: 'connect-airbnb' as const,
          title: 'Connect to Airbnb',
          description: 'Link Airbnb to manage all your listings directly from AdvanceCM.',
          ctaIcon: 'fab fa-airbnb',
          ctaText: 'Connect to Airbnb',
          action: () => this.connectAirbnb(),
          noSkip: true,
          async: true,
          completedEvent: 'onboarding_flow_connect_airbnb',
          canceledEvent: 'onboarding_flow_connect_airbnb_canceled',
        },
        {
          // TV4-5909: not gated behind connect-airbnb -- a Booking.com-only host must be
          // able to start here (the Airbnb task is noSkip, so the gate was a dead end).
          id: 'connect-booking' as const,
          title: 'Connect to Booking.com',
          description: 'Link Booking.com properties to manage all your listings directly from AdvanceCM.',
          ctaIconImage: 'assets/images/onboarding/bookingcom.webp',
          ctaText: 'Connect to Booking.com',
          action: () => this.connectBookingCom(),
          async: true,
          completedEvent: 'onboarding_flow_connect_booking',
          skippedEvent: 'onboarding_flow_connect_booking_skipped',
          canceledEvent: 'onboarding_flow_connect_booking_canceled',
        },
      ],
    },
    {
      title: 'Optimize business',
      icon: 'assets/images/onboarding/step2.svg',
      videoImage: 'assets/images/onboarding/ai.png',
      videoId: 'dljB0aIP52Q',
      tasks: [
        {
          id: 'intelligent-pricing' as const,
          title: 'Boost revenue with intelligent pricing',
          description: $localize`Leverage our AI-driven price optimization to maximize bookings and profits effortlessly.`,
          ctaIcon: 'cs-icon cs-icon-rategenie',
          ctaText: 'View Rategenie',
          action: () => this.viewRategenie(),
          async: true,
          completedEvent: 'onboarding_flow_intelligent_pricing',
          skippedEvent: 'onboarding_flow_intelligent_pricing_skipped',
          canceledEvent: 'onboarding_flow_intelligent_pricing_canceled',
        },
        {
          id: 'simplify-arrival' as const,
          title: 'Simplify every guest arrival',
          description: $localize`Share check-in details automatically so guests arrive smoothly every time.`,
          ctaText: 'Setup Instructions',
          // route: '/automations',
          action: () =>
            this.store.dispatch(
              OpenAutomationWizardOverlay({
                data: { event: AutomationEventTypes.TimeBeforeBookingCheckIn, type: AutomationTypes.Booking },
              })
            ),
          completedEvent: 'onboarding_flow_simplify_arrival',
          skippedEvent: 'onboarding_flow_simplify_arrival_skipped',
          canceledEvent: 'onboarding_flow_simplify_arrival_canceled',
        },
        {
          id: 'visitors-to-guests' as const,
          title: 'Turn visitors into guests',
          description: $localize`Use your booking website to convert more traffic into direct reservations.`,
          ctaText: 'Build Website',
          action: () => {
            this.router.navigate(['/websites'])
            this.store.dispatch(OpenAddWebsiteDialog({ data: { type: WebsiteTypes.RentalWebsite } }))
          },
          completedEvent: 'onboarding_flow_build_website',
          skippedEvent: 'onboarding_flow_build_website_skipped',
          canceledEvent: 'onboarding_flow_build_website_canceled',
        },
        {
          id: 'mobile-app' as const,
          title: 'Work on-the-go with the AdvanceCM app',
          description: $localize`Manage bookings, messages, and tasks anywhere from the mobile app.`,
          ctaText: 'Download the App',
          action: () => {
            this.markTaskAsCompleted(this.getTaskById('mobile-app')!)
            this.downloadApp()
          },
          completedEvent: 'onboarding_flow_download_app',
          skippedEvent: 'onboarding_flow_download_app_skipped',
          canceledEvent: 'onboarding_flow_download_app_canceled',
        },
      ],
    },
    {
      title: 'Automate tasks',
      icon: 'assets/images/onboarding/step3.svg',
      videoImage: 'assets/images/onboarding/automation.png',
      videoId: '7QnqZK0wSKs',
      tasks: [
        {
          id: 'automations' as const,
          title: $localize`Automation enhances your experience`,
          description: $localize`AI-driven smart replies and automation tools ensure your guests receive top-notch care 24/7`,
          ctaIcon: 'cs-icon cs-icon-autopilot',
          ctaText: 'Autopilot Wizard',
          action: () => {
            this.markTaskAsCompleted(this.getTaskById('automations')!)
            this.store.dispatch(OpenAutomationWizardOverlay({ data: {} }))
          },
          completedEvent: 'onboarding_flow_build_automation_created',
          skippedEvent: 'onboarding_flow_build_automation_skipped',
          canceledEvent: 'onboarding_flow_build_automation_canceled',
        },
        {
          id: 'auto-cleaning' as const,
          title: $localize`Automate your vendor workflow`,
          description: $localize`Auto-assign tasks, track progress, and keep turnovers running smoothly without manual follow-ups.`,
          ctaIcon: 'cs-icon cs-icon-cleaning',
          ctaText: 'Add a vendor',
          action: () => {
            this.router.navigate(['/vendors'])
            this.store.dispatch(OpenAddVendorDialog())
          },
          completedEvent: 'onboarding_flow_add_vendor',
          skippedEvent: 'onboarding_flow_add_vendor_skipped',
          canceledEvent: 'onboarding_flow_add_vendor_canceled',
        },
        {
          id: 'auto-communication' as const,
          title: $localize`Let AI handle your guest communication`,
          description: $localize`Enable AI-powered replies that instantly answer questions, share instructions, and keep guests informed.`,
          ctaText: 'Enable AI',
          action: () => this.enableAI(),
          async: true,
          completedEvent: 'onboarding_flow_enable_ai',
          skippedEvent: 'onboarding_flow_enable_ai_skipped',
          canceledEvent: 'onboarding_flow_enable_ai_canceled',
        },
        {
          id: 'auto-reviews' as const,
          title: $localize`Win more 5-star reviews automatically`,
          description: $localize`Trigger timely review requests and let automation boost your ratings on every booking.`,
          ctaText: 'Boost Reviews',
          // route: '/automations',
          action: () => {
            this.markTaskAsCompleted(this.getTaskById('auto-reviews')!)
            this.store.dispatch(
              OpenAutomationWizardOverlay({
                data: { event: AutomationEventTypes.TimeAfterBookingCheckout, type: AutomationTypes.Booking },
              })
            )
          },
          async: true,
          completedEvent: 'onboarding_flow_boost_reviews',
          skippedEvent: 'onboarding_flow_boost_reviews_skipped',
          canceledEvent: 'onboarding_flow_boost_reviews_canceled',
        },
      ],
    },
  ]

  selectedSection: OnboardingSection = this.sections[0]

  onboradingState: Record<string, boolean> = {}

  reviewAutomationGroup: AutomationViewGroup | undefined
  preCheckinMessageAutomationGroup: AutomationViewGroup | undefined

  isComplete = false

  isNewAccountIn7Days = false

  constructor(
    private store: Store<any>,
    private router: Router,
    private actions$: Actions,
    private aiSettings: AIResponderSettingsService,
    private channelConnectWizardsService: ChannelConnectWizardsService,
    private downloadAppService: DownloadAppService,
    private subscribeRategenieService: SubscribeRategenieService,
    private dataChecker: DataCheckerService,
    private helpVideoDialogService: HelpVideoDialogService,
    private googleTagManager: GoogleTagManagerService,
    private amplitude: AmplitudeService
  ) {
    super()
    this.dataChecker.check([VendorsGuard, AISettingsGuard, WebsiteGuard, ConnectionGuard])
  }

  ngOnInit(): void {
    this.store
      .pipe(
        select(selectCurrentAccount),
        filter(isSomething),
        tap((account) => {
          const accountCreatedDate = moment(account.created * 1000)
          const sevenDaysAgo = moment().subtract(7, 'days').startOf('day')
          this.isNewAccountIn7Days = accountCreatedDate.isAfter(sevenDaysAgo)

          this.onboradingState = account?.attributes?.onboarding || {}
          this.setTaskCompleteLocally('automations', this.onboradingState['automations'] || false)
          this.setTaskCompleteLocally('mobile-app', this.onboradingState['mobile-app'] || false)
          this.setTaskCompleteLocally('auto-reviews', this.onboradingState['auto-reviews'] || false)
          this.trackEvent('onboarding_viewed')
        }),
        switchMap((account) =>
          combineLatest([
            this.store.pipe(
              select(selectActiveProductPlan, { product: ProductsForPlan.Rategenie }),
              map((plan) => isSubscriptionValid(plan)),
              tap((valid) => {
                this.setTaskCompleteLocally('intelligent-pricing', this.onboradingState['intelligent-pricing'] || valid)
              })
            ),
            this.store.pipe(
              select(selectAutomationGroupsByEvent(AutomationEventTypes.TimeBeforeBookingCheckIn)),
              map((items) =>
                lodash.filter(
                  items,
                  (t) =>
                    t.handler === AutomationHandler.SendEmailMessage &&
                    t.event === AutomationEventTypes.TimeBeforeBookingCheckIn &&
                    AutomationHelpers.sendToGuest(t.items)
                )
              ),
              tap((items) => {
                this.preCheckinMessageAutomationGroup = lodash.first(items)
                this.setTaskCompleteLocally(
                  'simplify-arrival',
                  this.onboradingState['simplify-arrival'] || items?.length > 0
                )
              })
            ),
            this.store.pipe(
              select(selectAllWebsites),
              tap((items) => {
                this.setTaskCompleteLocally(
                  'visitors-to-guests',
                  this.onboradingState['visitors-to-guests'] || items?.length > 0
                )
              })
            ),
            this.store.pipe(
              select(selectAPIConnectionsByChannelName(ChannelNameTokens.AirBnBV2API)),
              tap((items) => {
                this.setTaskCompleteLocally(
                  'connect-airbnb',
                  this.onboradingState['connect-airbnb'] || items?.length > 0
                )
              })
            ),
            this.store.pipe(
              select(selectAPIConnectionsByChannelName(ChannelNameTokens.BookingAPI)),
              tap((items) => {
                this.setTaskCompleteLocally(
                  'connect-booking',
                  this.onboradingState['connect-booking'] || items?.length > 0
                )
              })
            ),
            // this.store.pipe(
            //   select(selectAutomationGroupsByHandler(AutomationHandler.ReviewSendAIReview)),
            //   tap((items) => {
            //     this.reviewAutomationGroup = lodash.first(items)
            //     this.setTaskCompleteLocally('auto-reviews', this.onboradingState['auto-reviews'] || items?.length > 0)
            //   })
            // ),
            this.store.pipe(
              select(selectAllVendors),
              tap((items) => {
                this.setTaskCompleteLocally('auto-cleaning', this.onboradingState['auto-cleaning'] || items?.length > 0)
              })
            ),
            this.store.pipe(
              select(selectAISettings),
              tap((settings) => {
                this.setTaskCompleteLocally(
                  'auto-communication',
                  this.onboradingState['auto-communication'] || settings?.isEnabled
                )
              })
            ),
          ])
        ),
        untilDestroy(this)
      )
      .subscribe(() => {
        this.refreshCompleteStatus()
      })

    this.actions$.pipe(ofType(AddAutomation), untilDestroy(this)).subscribe((action) => {
      const task = this.getTaskById('auto-reviews')
      if (action.payload.handler === AutomationHandler.SendReview && task?.completedEvent) {
        this.trackEvent(task.completedEvent)
      }
    })
  }

  trackEvent(event: string) {
    this.googleTagManager.trackEvent(event, { user_type: this.getUserType() })
    this.amplitude.logEvent(event)
  }

  refreshCompleteStatus() {
    const wasComplete = this.isComplete
    this.isComplete = this.totalCompleted === this.totalSteps
    this.completedTaskIds = this.getTasks()
      .filter((task) => task.completed)
      .map((task) => task.id)
    if (!wasComplete && this.isComplete) {
      this.trackEvent('onboarding_completed')
    }
  }

  get totalCompleted(): number {
    return this.getTasks().filter((task) => task.completed).length
  }

  get totalSteps(): number {
    return this.getTasks().length
  }

  get overallProgress(): number {
    const steps = this.totalSteps || 1
    return Math.round((this.totalCompleted / steps) * 100)
  }

  selectSection(section: OnboardingSection): void {
    this.selectedSection = section
    this.trackEvent('onboarding_section_viewed')
  }

  trackBySection = (_: number, section: OnboardingSection): string => section.title
  trackByTask = (_: number, task: OnboardingTask): string => task.id

  getCompletedSteps(section: OnboardingSection): number {
    return section.tasks.filter((task) => task.completed).length
  }

  onCompleteTask(task: OnboardingTask): void {
    if (task.completedEvent && !task.async) {
      this.trackEvent(task.completedEvent)
    }
    if (task.action) {
      task.action()
      return
    }

    this.markTaskAsCompleted(task)
    if (task.route) {
      this.router.navigate([task.route])
    }
  }

  onSkipTask(task: OnboardingTask): void {
    if (task.skippedEvent) {
      this.trackEvent(task.skippedEvent)
    }
    this.markTaskAsCompleted(task)
    this.openNextTask(task.id)
  }

  private markTaskAsCompleted(task: OnboardingTask): void {
    if (!task) {
      return
    }
    this.completedTaskIds.push(task.id)
    this.persistCompletedTasks()
  }

  private setTaskCompleteLocally(id: string, completed = true): void {
    const task = this.getTaskById(id)
    if (task) {
      task.completed = completed
    }
  }
  private getUserType() {
    return this.onboradingState['user_type'] ?? this.isNewAccountIn7Days ? 'last_7_days' : 'others'
  }

  private persistCompletedTasks(): void {
    const user_type = this.getUserType()
    this.store.dispatch(
      updateAccountAttributes({
        attributes: {
          onboarding: { ...Object.fromEntries(this.completedTaskIds?.map((id) => [id, true])), user_type },
        },
      })
    )
  }

  isPretaskCompleted(task: OnboardingTask): boolean {
    if (!task.pretaskId) return true
    const preTask = this.getTaskById(task.pretaskId)
    if (!preTask) return true
    return preTask.completed
  }

  getTasks(): OnboardingTask[] {
    return this.sections.map((section) => section.tasks).flat()
  }

  getTaskById(id: string): OnboardingTask | undefined {
    return this.getTasks().find((task) => task.id === id)
  }

  private openNextTask(currentTaskId: string): void {
    const tasks = this.getTasks()
    const currentTaskIndex = tasks.findIndex((task) => task.id === currentTaskId)
    if (currentTaskIndex === -1 || currentTaskIndex === tasks.length - 1) {
      return
    }
    const nextTask = tasks[currentTaskIndex + 1]
    if (!nextTask) {
      return
    }
    const nextSection = this.sections.find((section) => section.tasks.includes(nextTask))
    this.selectSection(nextSection)
    setTimeout(() => this.taskAccordion?.expand(nextTask.id))
  }

  connectAirbnb() {
    const task = this.getTaskById('connect-airbnb')
    this.store
      .pipe(
        selectSomeOnce(selectChannelByName, { name: ChannelNameTokens.AirBnBV2API, type: ChannelTypes.Auto }),
        switchMap((ch) => this.channelConnectWizardsService.connect(ch))
      )
      .subscribe((result) => {
        if (!result && task?.canceledEvent) {
          this.trackEvent(task.canceledEvent)
        }
        if (result && task?.completedEvent) {
          this.trackEvent(task.completedEvent)
        }
      })
  }

  connectBookingCom() {
    const task = this.getTaskById('connect-booking')
    this.store
      .pipe(
        selectSomeOnce(selectChannelByName, { name: ChannelNameTokens.BookingAPI, type: ChannelTypes.Auto }),
        switchMap((ch) => this.channelConnectWizardsService.connect(ch))
      )
      .subscribe((result) => {
        if (!result && task?.canceledEvent) {
          this.trackEvent(task.canceledEvent)
        }
        if (result && task?.completedEvent) {
          this.trackEvent(task.completedEvent)
        }
      })
  }

  downloadApp() {
    this.downloadAppService.open().afterClosed().subscribe()
  }

  viewRategenie() {
    const task = this.getTaskById('intelligent-pricing')
    this.subscribeRategenieService
      .open()
      .afterClosed()
      .subscribe((result) => {
        if (!result && task?.canceledEvent) {
          this.trackEvent(task.canceledEvent)
        }
        if (result && task?.completedEvent) {
          this.trackEvent(task.completedEvent)
        }
      })
  }

  enableAI() {
    const task = this.getTaskById('auto-communication')
    this.aiSettings.open().subscribe((result) => {
      if (!result && task?.canceledEvent) {
        this.trackEvent(task.canceledEvent)
      }
      if (result && task?.completedEvent) {
        this.trackEvent(task.completedEvent)
      }
    })
  }

  onViewVideo(videoId: string) {
    this.trackEvent('onboarding_video_watched')
    this.helpVideoDialogService.open(videoId)
  }
}
